import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { newsList } from '@/mocks/news';
import PageHero from '@/components/feature/PageHero';
import NewsListItem from '@/components/feature/NewsListItem';
import Pagination from '@/components/feature/Pagination';
import EmptyState from '@/components/feature/EmptyState';
import HotRanking from '@/pages/home/components/HotRanking';

const PAGE_SIZE = 8;

type SortKey = 'latest' | 'hot';

export default function NewsPage() {
  usePageMeta({
    title: '娱乐新闻 - 靠逼',
    description: '靠逼娱乐新闻频道实时更新娱乐圈最新动态、明星动态与重磅事件，第一时间掌握行业要闻。',
    keywords: '娱乐新闻,明星动态,娱乐圈,靠逼',
  });

  const [sort, setSort] = useState<SortKey>('latest');
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => {
    const list = [...newsList];
    if (sort === 'hot') return list.sort((a, b) => b.views - a.views);
    return list.sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  }, [sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hot = useMemo(() => [...newsList].sort((a, b) => b.views - a.views).slice(0, 5), []);

  const changeSort = (key: SortKey) => {
    setSort(key);
    setPage(1);
  };

  const sortOptions: { key: SortKey; label: string; icon: string }[] = [
    { key: 'latest', label: '时间排序', icon: 'ri-time-line' },
    { key: 'hot', label: '热点排序', icon: 'ri-fire-line' },
  ];

  return (
    <div className="w-full">
      <PageHero
        title="娱乐新闻"
        subtitle="追踪娱乐圈最新动态，明星、作品与行业事件一网打尽"
        crumbs={[{ label: '娱乐新闻' }]}
        icon="ri-newspaper-line"
      />

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-500">
                共 <span className="font-semibold text-foreground-800">{sorted.length}</span> 条资讯
              </p>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => changeSort(opt.key)}
                    className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      sort === opt.key
                        ? 'bg-background-50 font-medium text-primary-600'
                        : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    <i className={opt.icon} />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {current.length === 0 ? (
              <EmptyState
                icon="ri-newspaper-line"
                title="暂无相关新闻，试试其他分类。"
                description="当前条件下没有找到内容，可以切换到其他栏目看看。"
                actionLabel="返回首页"
                actionTo="/"
              />
            ) : (
              <div className="flex flex-col gap-3 md:gap-4">
                {current.map((item) => (
                  <NewsListItem key={item.id} item={item} />
                ))}
              </div>
            )}

            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </div>

          <aside className="flex flex-col gap-6">
            <HotRanking items={hot} />
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="font-heading text-base font-bold text-foreground-950">浏览其他栏目</h3>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/movies"
                  className="flex items-center gap-2 rounded-md border border-background-100 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-film-line" />
                  影视推荐
                </Link>
                <Link
                  to="/hot-topics"
                  className="flex items-center gap-2 rounded-md border border-background-100 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-fire-line" />
                  热门话题
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}